import { useState } from 'react';
import { Link } from 'react-router-dom';
import { getSupabase } from '../../../lib/supabase';
import { getVehicleRequestSchema } from '../../../schemas/vehicleRequestSchema';
import type { VehicleRequestData } from '../../../schemas/vehicleRequestSchema';

const YEARS = Array.from({ length: 2027 - 1995 + 1 }, (_, i) => 2027 - i);

export default function RequestVehicleForm() {
  const [formData, setFormData] = useState<VehicleRequestData>({
    name: '',
    email: '',
    phone: '',
    make: '',
    model: '',
    year_min: '',
    year_max: '',
    body_type: '',
    budget_min: '',
    budget_max: '',
    max_mileage: '',
    details: '',
    preferred_color: '',
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSubmitError(null);

    try {
      // Validate with Zod
      const schema = await getVehicleRequestSchema();
      const validatedData = schema.parse(formData);

      // Submit to Supabase
      const supabase = await getSupabase();
      
      const leadData = {
        name: validatedData.name.trim(),
        email: validatedData.email.trim(),
        phone: validatedData.phone?.trim() || null,
        source: 'deal-radar-request',
        preferred_make: validatedData.make.trim(),
        preferred_model: validatedData.model.trim(),
        year_range: validatedData.year_min && validatedData.year_max 
          ? `${validatedData.year_min}-${validatedData.year_max}`
          : validatedData.year_min || validatedData.year_max || null,
        body_type: validatedData.body_type || null,
        budget: validatedData.budget_min && validatedData.budget_max
          ? `$${validatedData.budget_min}-$${validatedData.budget_max}`
          : validatedData.budget_min
          ? `$${validatedData.budget_min}+`
          : validatedData.budget_max
          ? `Up to $${validatedData.budget_max}`
          : null,
        max_mileage: validatedData.max_mileage ? parseInt(validatedData.max_mileage, 10) : null,
        notes: validatedData.details?.trim() || null,
        status: 'new',
      };

      const { error: insertError } = await supabase
        .from('leads')
        .insert([leadData]);

      if (insertError) {
        console.error('Supabase insert error:', insertError);
        setSubmitError('Something went wrong. Please try again or give us a call.');
        setIsLoading(false);
        return;
      }

      // Success
      setIsSubmitted(true);
    } catch (err: any) {
      console.error('Vehicle request submission error:', err);
      
      // Handle Zod validation errors
      if (err.errors) {
        const fieldErrors: Record<string, string> = {};
        err.errors.forEach((error: any) => {
          const field = error.path[0];
          fieldErrors[field] = error.message;
        });
        setErrors(fieldErrors);
      } else {
        setSubmitError('Connection issue. Check your internet and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <section
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="bg-gradient-to-br from-[#1C2B1E] to-[#2a3f2c] py-16 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-3xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm border-2 border-[#E8B84B]/30 rounded-2xl p-8 sm:p-12 text-center">
            <div className="w-20 h-20 bg-[#E8B84B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-5xl text-[#E8B84B]"></i>
            </div>
            
            <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              We're On It. 🎯
            </h3>
            
            <p className="text-lg text-white/80 mb-6 leading-relaxed">
              Your request for a <strong className="text-[#E8B84B]">{formData.make} {formData.model}</strong> is now in our system. 
              We'll start hunting and reach out within 24 hours with options.
            </p>

            <div className="bg-white/10 border border-white/20 rounded-xl p-6 mb-8 text-left">
              <h4 className="text-sm font-bold text-white/60 uppercase tracking-wide mb-3">What Happens Next</h4>
              <ul className="space-y-3 text-white/80 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-search-line text-[#E8B84B] text-lg flex-shrink-0 mt-0.5"></i>
                  <span>We'll search our network of trusted sources for vehicles matching your criteria</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-phone-line text-[#E8B84B] text-lg flex-shrink-0 mt-0.5"></i>
                  <span>You'll hear from us within 24 hours with options and pricing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-car-line text-[#E8B84B] text-lg flex-shrink-0 mt-0.5"></i>
                  <span>If we find the perfect match, we'll handle inspection, negotiation, and delivery</span>
                </li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/deal-radar"
                className="inline-flex items-center justify-center gap-2 bg-[#E8B84B] text-[#1C2B1E] hover:bg-[#d4a43a] font-bold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap text-sm"
              >
                <i className="ri-notification-line text-lg"></i>
                Set Up Deal Alerts
              </Link>
              
              <Link
                to="/inventory"
                className="inline-flex items-center justify-center gap-2 bg-white/10 text-white hover:bg-white/20 border-2 border-white/20 font-bold px-6 py-3 rounded-lg transition-colors duration-200 whitespace-nowrap text-sm"
              >
                <i className="ri-car-line text-lg"></i>
                Browse Current Inventory
              </Link>
            </div>

            <p className="text-xs text-white/70 mt-8 italic">
              Need to reach us faster? Call <a href="tel:+18554686348" className="text-[#E8B84B] hover:underline font-semibold">(855) 468-6348</a>
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-[#1C2B1E] to-[#2a3f2c] py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E8B84B]/20 rounded-full mb-4">
            <i className="ri-search-car-line text-3xl text-[#E8B84B]"></i>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            We'll hunt it down. Tell us what you want and we'll tap our network to find it — no obligation, no pressure.
          </p>
        </div>

        <div className="bg-white/5 backdrop-blur-sm border-2 border-white/10 rounded-2xl p-6 sm:p-8">
          <form 
            id="deal-radar-vehicle-request-form"
            data-readdy-form
            onSubmit={handleSubmit} 
            className="space-y-6"
            action="https://readdy.ai/api/form/d6m4bttv117fnkj2h18g"
            method="POST"
          >
            {/* Global Error */}
            {submitError && (
              <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="bg-red-500/10 border-2 border-red-500/30 rounded-lg p-4 flex items-start gap-3"
              >
                <i className="ri-error-warning-line text-xl text-red-400 flex-shrink-0 mt-0.5" aria-hidden="true"></i>
                <div>
                  <p className="text-sm font-semibold text-red-300 mb-1">Oops!</p>
                  <p className="text-sm text-red-200/80">{submitError}</p>
                </div>
              </div>
            )}

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <i className="ri-user-line text-[#E8B84B]"></i>
                Your Contact Info
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
                    Name <span className="text-[#E8B84B]">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.name ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/10'
                    } text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.name}
                    disabled={isLoading}
                  />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                    Email <span className="text-[#E8B84B]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.email ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/10'
                    } text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.email}
                    disabled={isLoading}
                  />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
                  Phone Number <span className="text-white/65 font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(503) 555-0100"
                  className={`w-full px-4 py-3 rounded-lg border-2 ${
                    errors.phone ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/10'
                  } text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm`}
                  aria-invalid={!!errors.phone}
                  disabled={isLoading}
                />
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="border-t border-white/10 pt-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <i className="ri-car-line text-[#E8B84B]"></i>
                What Vehicle Are You Looking For?
              </h3>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="make" className="block text-sm font-medium text-white/80 mb-2">
                    Make <span className="text-[#E8B84B]">*</span>
                  </label>
                  <input
                    type="text"
                    id="make"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    placeholder="e.g., Toyota"
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.make ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/10'
                    } text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.make}
                    disabled={isLoading}
                  />
                  {errors.make && <p className="text-red-400 text-xs mt-1">{errors.make}</p>}
                </div>

                <div>
                  <label htmlFor="model" className="block text-sm font-medium text-white/80 mb-2">
                    Model <span className="text-[#E8B84B]">*</span>
                  </label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="e.g., Tacoma"
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.model ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/10'
                    } text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm`}
                    required
                    aria-required="true"
                    aria-invalid={!!errors.model}
                    disabled={isLoading}
                  />
                  {errors.model && <p className="text-red-400 text-xs mt-1">{errors.model}</p>}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <fieldset className="border-0 p-0 m-0">
                    <legend className="block text-sm font-medium text-white/80 mb-2">
                      Year Range <span className="text-white/65 font-normal">(optional)</span>
                    </legend>
                    <div className="flex items-center gap-3">
                      <div className="w-full">
                        <label htmlFor="year_min" className="sr-only">Year from</label>
                        <select
                          id="year_min"
                          name="year_min"
                          value={formData.year_min}
                          onChange={handleChange}
                          aria-label="Year from"
                          className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm cursor-pointer"
                          disabled={isLoading}
                        >
                          <option value="">From</option>
                          {YEARS.map((y) => (
                            <option key={y} value={String(y)} className="bg-[#1C2B1E] text-white">{y}</option>
                          ))}
                        </select>
                      </div>
                      <span className="text-white/65 text-sm flex-shrink-0" aria-hidden="true">to</span>
                      <div className="w-full">
                        <label htmlFor="year_max" className="sr-only">Year to</label>
                        <select
                          id="year_max"
                          name="year_max"
                          value={formData.year_max}
                          onChange={handleChange}
                          aria-label="Year to"
                          className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm cursor-pointer"
                          disabled={isLoading}
                        >
                          <option value="">To</option>
                          {YEARS.map((y) => (
                            <option key={y} value={String(y)} className="bg-[#1C2B1E] text-white">{y}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </fieldset>
                </div>

                <div>
                  <label htmlFor="body_type" className="block text-sm font-medium text-white/80 mb-2">
                    Body Type <span className="text-white/65 font-normal">(optional)</span>
                  </label>
                  <select
                    id="body_type"
                    name="body_type"
                    value={formData.body_type}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm cursor-pointer"
                    disabled={isLoading}
                  >
                    <option value="">Any Type</option>
                    <option value="Sedan">Sedan</option>
                    <option value="SUV">SUV</option>
                    <option value="Truck">Truck</option>
                    <option value="Coupe">Coupe</option>
                    <option value="Hatchback">Hatchback</option>
                    <option value="Wagon">Wagon</option>
                    <option value="Van">Van</option>
                    <option value="Convertible">Convertible</option>
                  </select>
                </div>
              </div>

              {/* Preferred Color */}
              <div className="mb-4">
                <label htmlFor="preferred_color" className="block text-sm font-medium text-white/80 mb-2">
                  Preferred Color <span className="text-white/65 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  id="preferred_color"
                  name="preferred_color"
                  value={formData.preferred_color || ''}
                  onChange={handleChange}
                  placeholder="e.g. White, Silver, Midnight Black..."
                  className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                  disabled={isLoading}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="budget_min" className="block text-sm font-medium text-white/80 mb-2">
                    Budget Range <span className="text-white/65 font-normal">(optional)</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="relative w-full">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/65 text-sm">$</span>
                      <input
                        type="number"
                        id="budget_min"
                        name="budget_min"
                        value={formData.budget_min}
                        onChange={handleChange}
                        placeholder="10000"
                        min="0"
                        step="1000"
                        className="w-full pl-8 pr-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                        disabled={isLoading}
                      />
                    </div>
                    <span className="text-white/65 text-sm">to</span>
                    <div className="relative w-full">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/65 text-sm">$</span>
                      <input
                        type="number"
                        id="budget_max"
                        name="budget_max"
                        value={formData.budget_max}
                        onChange={handleChange}
                        placeholder="30000"
                        min="0"
                        step="1000"
                        className="w-full pl-8 pr-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                        disabled={isLoading}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="max_mileage" className="block text-sm font-medium text-white/80 mb-2">
                    Max Mileage <span className="text-white/65 font-normal">(optional)</span>
                  </label>
                  <input
                    type="number"
                    id="max_mileage"
                    name="max_mileage"
                    value={formData.max_mileage}
                    onChange={handleChange}
                    placeholder="e.g., 100000"
                    min="0"
                    step="1000"
                    className="w-full px-4 py-3 rounded-lg border-2 border-white/10 bg-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            {/* Additional Details */}
            <div className="border-t border-white/10 pt-6">
              <label htmlFor="details" className="block text-sm font-medium text-white/80 mb-2">
                Additional Details <span className="text-white/65 font-normal">(optional — max 500 characters)</span>
              </label>
              <textarea
                id="details"
                name="details"
                value={formData.details}
                onChange={handleChange}
                placeholder="Any specific features, colors, or requirements? Let us know..."
                maxLength={500}
                rows={4}
                aria-describedby="request-details-count"
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.details ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/10'
                } text-white placeholder-white/40 focus:outline-none focus:border-[#E8B84B]/60 transition-colors text-sm resize-none`}
                aria-invalid={!!errors.details}
                disabled={isLoading}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.details && <p className="text-red-400 text-xs">{errors.details}</p>}
                <p id="request-details-count" className="text-white/65 text-xs ml-auto" aria-live="polite">{formData.details?.length || 0}/500</p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#E8B84B] text-[#1C2B1E] hover:bg-[#d4a43a] font-bold py-4 rounded-lg transition-colors duration-200 whitespace-nowrap text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E8B84B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1C2B1E]"
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line text-xl animate-spin"></i>
                  <span>Submitting Request...</span>
                </>
              ) : (
                <>
                  <i className="ri-send-plane-fill text-lg"></i>
                  <span>Submit Vehicle Request</span>
                </>
              )}
            </button>

            <p className="text-xs text-white/70 text-center italic">
              No obligation. We'll reach out within 24 hours with options — or let you know if we can't find it.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}